import React, {useEffect, useMemo, useState} from 'react'
import {Container, Row} from 'react-bootstrap';
import BreadCrumb from '../../Common/BreadCrumb';
import PaymentActivity from './PaymentActivity';

import {useTranslation} from 'react-i18next';
import Overview from "./Overview";
import TopProducts from "./TopProducts";
import TopCashier from "./TopCashier";
import {useSelector} from "react-redux";
import {parseTRDate} from "../../utils/textUtils";
import {dashboardAPI} from "../../services/api";


const Dashboard = () => {
    const {t} = useTranslation();
    document.title = t('Dashboard.title');
    const today = new Date();
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(today.getDate() - 7);

    const alerts = useSelector((state: any) => state.Transactions.alerts);
    const transactions = useSelector((state: any) => state.Transactions.transactions);

    const [selectedCity, setSelectedCity] = useState<OptionType | null>(null);
    const [selectedProvince, setSelectedProvince] = useState<OptionType | null>(null);
    const [selectedStore, setSelectedStore] = useState<OptionType | null>(null);

    const [fromDate, setFromDate] = useState<Date>(oneWeekAgo);
    const [toDate, setToDate] = useState<Date>(today);
    const [alertSeries, setAlertSeries] = useState<number[]>([]);

    const [scenarioSeries, setScenarioSeries] = useState<number[]>([]);
    const [ruleSeries, setRuleSeries] = useState<number[]>([]);
    const [totalAmount, setTotalAmount] = useState(0);
    const [saleAmounts, setSaleAmounts] = useState<number[]>([]);
    const [saleDates, setSaleDates] = useState<string[]>([]);

    const [cashiers, setCashiers] = useState<string[]>([]);
    const [cashierTransactions, setCashierTransactions] = useState<number[]>([]);

    const [products, setProducts] = useState<string[]>([]);
    const [productAmounts, setProductAmounts] = useState<number[]>([]);
    
    // Loading state for backend data
    const [isLoadingStats, setIsLoadingStats] = useState(false);
    const [backendStats, setBackendStats] = useState<any>(null);
    const [backendStores, setBackendStores] = useState<any[]>([]);
    
    // Check if user is demo user
    const isDemoUser = useMemo(() => {
        const authUser = sessionStorage.getItem("authUser");
        if (!authUser) return false;
        try {
            const userData = JSON.parse(authUser);
            const token = userData.token || userData.access_token;
            return token?.startsWith('demo-token-') || userData.email === 'admin@demo.com.tr';
        } catch {
            return false;
        }
    }, []);

    const filteredTransactions = useMemo(() => {
        // For real users with backend stats, don't use Redux mock data
        if (!isDemoUser && backendStats) {
            console.log('🚫 Using empty filteredTransactions - backend stats available');
            return [];
        }
        
        // For demo users or when backend stats not yet loaded, use Redux transactions
        return transactions.filter((tx: any) => {
            const txDate = parseTRDate(tx.date);

            const matchesStore =
                !selectedStore || selectedStore.value === "" || tx.store === selectedStore.value;

            const matchesProvince =
                !selectedProvince || selectedProvince.value === "" || tx.storeData?.province === selectedProvince.value;

            const matchesCity =
                !selectedCity || selectedCity.value === "" || tx.storeData?.city === selectedCity.value;

            const matchesDate =
                (!fromDate || txDate >= fromDate) &&
                (!toDate || txDate <= toDate);

            return matchesStore && matchesProvince && matchesCity && matchesDate;
        });

    }, [transactions, selectedCity, selectedProvince, selectedStore, fromDate, toDate, isDemoUser, backendStats]);



    useEffect(() => {
        // CRITICAL: Never calculate from mock data if we have backend stats (for real users)
        if (!isDemoUser && backendStats) {
            console.log('🚫 Skipping totalAmount calculation - using backend stats');
            return;
        }
        
        // Only calculate for demo users
        if (!isDemoUser) {
            return; // Wait for backend stats
        }
        
        console.log('📊 Calculating totalAmount from mock data (demo user)');
        console.log('📊 filteredTransactions count:', filteredTransactions.length);
        // Local calculation for demo users only
        const total = filteredTransactions.reduce((acc:any, tx:any) => {
            // Handle both "₺123.45" and "123.45" formats, also handle comma separators
            const totalStr = tx.total || "0";
            const numeric = parseFloat(totalStr.toString().replace(/[₺,\s]/g, "") || "0");
            return acc + (isNaN(numeric) ? 0 : numeric);
        }, 0);
        const roundedTotal = parseFloat(total.toFixed(2));
        console.log('📊 Calculated totalAmount:', roundedTotal);
        setTotalAmount(roundedTotal);
    }, [filteredTransactions, isDemoUser, backendStats]);

    useEffect(() => {
        // CRITICAL: Never calculate from mock data if we have backend stats (for real users)
        if (!isDemoUser && backendStats) {
            console.log('🚫 Skipping saleDates/saleAmounts calculation - using backend stats');
            return;
        }
        
        // Only calculate for demo users
        if (!isDemoUser) {
            return; // Wait for backend stats
        }
        
        console.log('📊 Calculating saleDates/saleAmounts from mock data (demo user)');
        // Local calculation for demo users only
        const grouped: { [date: string]: number } = {};

        filteredTransactions.forEach((tx:any) => {
            const date = tx.date.split(" ")[0];
            const amount = parseFloat(tx.total?.replace("₺", "") || "0");
            grouped[date] = (grouped[date] || 0) + amount;
        });

        const dates = Object.keys(grouped).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
        const amounts = dates.map(date => parseFloat(grouped[date].toFixed(2)));

        setSaleDates(dates);
        setSaleAmounts(amounts);
    }, [filteredTransactions, isDemoUser, backendStats]);


    useEffect(() => {
        // Skip if we have backend stats (for real users) - cashier data not provided by backend
        if (!isDemoUser && backendStats) {
            // For real users, show empty data since backend doesn't provide cashier stats
            setCashiers([]);
            setCashierTransactions([]);
            return;
        }
        
        // Local calculation for demo users only
        const cashierCounts: Record<string, number> = {};

        filteredTransactions.forEach((tx: any) => {
            if (cashierCounts[tx.cashier]) {
                cashierCounts[tx.cashier]++;
            } else {
                cashierCounts[tx.cashier] = 1;
            }
        });

        const sortedCashiers = Object.entries(cashierCounts)
            .sort((a, b) => b[1] - a[1])         // Sort descending
            .slice(0, 10);                        // Top 10 only

        setCashiers(sortedCashiers.map(([name]) => name));
        setCashierTransactions(sortedCashiers.map(([, count]) => count));
    }, [filteredTransactions, isDemoUser, backendStats]);


    useEffect(() => {
        // Skip if we have backend stats (for real users) - product data not provided by backend
        if (!isDemoUser && backendStats) {
            // For real users, show empty data since backend doesn't provide product stats
            setProducts([]);
            setProductAmounts([]);
            return;
        }
        
        // Local calculation for demo users only
        const grouped: { [product: string]: number } = {};

        filteredTransactions.forEach((tx:any) => {
            tx.items?.forEach((item: any) => {
                grouped[item.name] = (grouped[item.name] || 0) + item.qty;
            });
        });

        const entries = Object.entries(grouped).sort((a, b) => b[1] - a[1]).slice(0, 10);
        setProducts(entries.map(e => e[0]));
        setProductAmounts(entries.map(e => e[1]));
    }, [filteredTransactions, isDemoUser, backendStats]);


    useEffect(() => {
        // CRITICAL: Never calculate from mock data if we have backend stats (for real users)
        if (!isDemoUser && backendStats) {
            console.log('🚫 Skipping alert/scenario/rule calculation - using backend stats');
            // Backend stats already set alertSeries, scenarioSeries, ruleSeries in fetchDashboardStats
            return;
        }
        
        // Only calculate for demo users
        if (!isDemoUser) {
            return; // Wait for backend stats
        }
        
        console.log('📊 Calculating alert/scenario/rule from mock data (demo user)');
        // Local calculation for demo users only
        const matchedReceiptNumbers = new Set(filteredTransactions.map((tx:any) => tx.receiptNumber));
        const filteredAlerts = alerts.filter((alert:Alert) => matchedReceiptNumbers.has(alert.receiptNumber));

        const counts: Record<AlertStatus, number> = {
            new: 0,
            in_review: 0,
            closed: 0,
        };
        filteredAlerts.forEach((a:Alert) => {
            counts[a.status as AlertStatus]++;
        });
        setAlertSeries([counts.new, counts.in_review, counts.closed]);

        setScenarioSeries([new Set(filteredAlerts.map((a:any) => a.scenarioId)).size]);
        setRuleSeries([new Set(filteredAlerts.map((a:any) => a.ruleId)).size]);

    }, [filteredTransactions, alerts, isDemoUser, backendStats]);

    // Fetch dashboard stats from backend for real users
    useEffect(() => {
        // Skip backend call for demo users - they use local calculation
        if (isDemoUser) {
            return;
        }

        const fetchDashboardStats = async () => {
            setIsLoadingStats(true);
            try {
                // Format dates to ISO string (YYYY-MM-DD) for backend
                // Use local date methods to avoid timezone issues
                const formatDate = (date: Date): string => {
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const day = String(date.getDate()).padStart(2, '0');
                    return `${year}-${month}-${day}`;
                };
                
                const fromDateStr = fromDate ? formatDate(fromDate) : undefined;
                const toDateStr = toDate ? formatDate(toDate) : undefined;
                
                // Get storeId if a store is selected
                const storeId = selectedStore?.value || undefined;
                
                console.log('📊 Fetching dashboard stats from backend...', { storeId, fromDateStr, toDateStr });
                
                const response: any = await dashboardAPI.getDashboardStats({
                    storeId,
                    from: fromDateStr,
                    to: toDateStr
                });
                
                // Normalize response - handle both AxiosResponse and direct data
                const stats = response?.data || response;
                
                console.log('✅ Dashboard stats received:', stats);
                setBackendStats(stats);
                
                // Map backend stats to component state
                if (stats) {
                    console.log('📊 Applying backend stats to component state...');
                    
                    // Alert series: [new, in_review, closed]
                    // Backend uses: openAlerts, inReviewAlerts, closedAlerts
                    const newAlertSeries = [
                        stats.openAlerts || 0,
                        stats.inReviewAlerts || 0,
                        stats.closedAlerts || 0
                    ];
                    console.log('📊 Setting alertSeries:', newAlertSeries);
                    setAlertSeries(newAlertSeries);
                    
                    // Scenario series: count of unique scenarios
                    // Backend provides alertsByScenario - count the keys
                    const scenarioCount = stats.alertsByScenario ? Object.keys(stats.alertsByScenario).length : 0;
                    console.log('📊 Setting scenarioSeries:', [scenarioCount]);
                    setScenarioSeries([scenarioCount]);
                    
                    // Rule series: count of unique rules
                    // Backend provides alertsByRule - count the keys
                    const ruleCount = stats.alertsByRule ? Object.keys(stats.alertsByRule).length : 0;
                    console.log('📊 Setting ruleSeries:', [ruleCount]);
                    setRuleSeries([ruleCount]);
                    
                    // Total amount from backend revenue
                    const newTotalAmount = stats.totalRevenue || 0;
                    console.log('📊 Setting totalAmount from backend:', newTotalAmount);
                    console.log('📊 Filter params - fromDate:', fromDateStr, 'toDate:', toDateStr, 'storeId:', storeId);
                    setTotalAmount(newTotalAmount);
                    
                    // Daily revenue trend for PaymentActivity chart
                    if (stats.dailyRevenueTrend) {
                        const dates = Object.keys(stats.dailyRevenueTrend).sort();
                        const amounts = dates.map(date => stats.dailyRevenueTrend[date] || 0);
                        console.log('📊 Setting saleDates:', dates);
                        console.log('📊 Setting saleAmounts:', amounts);
                        setSaleDates(dates);
                        setSaleAmounts(amounts);
                    } else {
                        // If no dailyRevenueTrend, set empty arrays
                        setSaleDates([]);
                        setSaleAmounts([]);
                    }
                }
            } catch (error: any) {
                console.error('❌ Failed to fetch dashboard stats:', error);
                // Fall back to local calculation on error
                setBackendStats(null);
            } finally {
                setIsLoadingStats(false);
            }
        };

        fetchDashboardStats();
    }, [isDemoUser, fromDate, toDate, selectedStore]);

    // Fetch stores from backend for real users
    useEffect(() => {
        // Skip backend call for demo users - they use mock stores
        if (isDemoUser) {
            return;
        }

        const fetchStores = async () => {
            try {
                console.log('📦 Fetching stores from backend...');
                const response: any = await dashboardAPI.getStores();
                
                // Normalize response - handle both AxiosResponse and direct data
                const stores = Array.isArray(response) ? response : (response?.data || []);
                
                console.log('✅ Stores received:', stores.length, 'stores');
                setBackendStores(stores);
            } catch (error: any) {
                console.error('❌ Failed to fetch stores:', error);
                setBackendStores([]);
            }
        };

        fetchStores();
    }, [isDemoUser]);





    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb
                        items={[
                            { label: t('Dashboard.header'), path: "/" }
                        ]}
                    />
                    <Overview
                        selectedCity={selectedCity}
                        setSelectedCity={setSelectedCity}
                        selectedProvince={selectedProvince}
                        setSelectedProvince={setSelectedProvince}
                        selectedStore={selectedStore}
                        setSelectedStore={setSelectedStore}
                        toDate={toDate}
                        fromDate={fromDate}
                        setToDate={setToDate}
                        setFromDate={setFromDate}
                        alertSeries={alertSeries}
                        scenarioSeries={scenarioSeries}
                        ruleSeries={ruleSeries}
                        backendStores={backendStores}
                        isDemoUser={isDemoUser}
                    />
                    <Row>
                        <PaymentActivity
                            totalAmount={totalAmount}
                            saleAmounts={saleAmounts}
                            saleDates={saleDates}/>

                        <TopCashier
                            dataColors='["--in-primary"]'
                            cashiers={cashiers}
                            cashierTransactions={cashierTransactions}
                        />
                    </Row>

                    <TopProducts
                        products={products}
                        productAmounts={productAmounts}
                    />
                </Container>
            </div>
        </React.Fragment>
    )
}

export default Dashboard